import type { FC, ReactNode } from 'react'
import type { ProductSummary } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { ArrowLeft, MapPin, MoveHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button-link'
import { SectionHeading } from '@/components/generals/section-heading'
import { ProductEmblem } from '@/components/products/product-emblem'
import { ProductPackage } from '@/components/three/product-package'
import { toPackageLabel } from '@/components/three/package-data'
import { MEDIA } from '@/constants/media'

interface ProductShowcaseProps {
    products: ProductSummary[]
}

/* Home page 3D showcase: pick a product and its package turns to show the new label */
export const ProductShowcase: FC<ProductShowcaseProps> = ({ products }): ReactNode => {
    const [selectedId, setSelectedId] = useState<string | null>(products[0]?.id ?? null)
    const selected = products.find(product => product.id === selectedId) ?? products[0] ?? null

    return (
        <section id='products' aria-labelledby='products-title' className='relative isolate overflow-hidden bg-forest-950 py-24 text-cream lg:py-32'>
            <Image
                src={MEDIA.heroProducts}
                alt=''
                fill
                sizes='100vw'
                className='-z-10 scale-110 object-cover opacity-25 blur-2xl'
            />

            <div aria-hidden='true' className='absolute inset-0 -z-10 bg-linear-to-b from-forest-950 via-forest-950/80 to-forest-950' />
            <div aria-hidden='true' className='bg-girih pointer-events-none absolute inset-0 -z-10 opacity-[0.05]' />

            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between'>
                    <SectionHeading
                        id='products-title'
                        tone='dark'
                        eyebrow='ویترین سه‌بعدی محصولات'
                        title='گنجینه گیاهان دارویی زاگرس'
                        description='محصولی را انتخاب کنید و بسته‌بندی آن را از هر زاویه ببینید. هر محموله پیش از بسته‌بندی در آزمایشگاه آنالیز می‌شود.'
                    />

                    <Link
                        href='/products'
                        className='inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-gold transition-colors hover:text-gold-light'
                    >
                        همه محصولات
                        <ArrowLeft aria-hidden='true' className='size-4' />
                    </Link>
                </div>

                {selected ? (
                    <div className='mt-14 grid items-center gap-12 lg:grid-cols-12 lg:gap-10'>
                        <div className='lg:col-span-6'>
                            <div className='relative mx-auto aspect-square w-full max-w-[32rem]'>
                                <div aria-hidden='true' className='absolute inset-x-[12%] bottom-[6%] h-[16%] rounded-[50%] border border-gold/30 bg-[radial-gradient(ellipse_at_center,rgba(201,162,74,0.35),rgba(15,42,30,0.9)_70%)] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)]' />
                                <div aria-hidden='true' className='absolute inset-x-[18%] bottom-[9%] h-[10%] rounded-[50%] border border-gold/20' />
                                <div aria-hidden='true' className='absolute inset-[14%] rounded-full bg-gold/10 blur-3xl' />

                                <ProductPackage
                                    label={toPackageLabel(selected)}
                                    fallback={(
                                        <div className='absolute inset-[12%] overflow-hidden rounded-[2rem]'>
                                            <ProductEmblem categorySlug={selected.category_slug} size='large' />
                                        </div>
                                    )}
                                />
                            </div>

                            <p className='mt-2 flex items-center justify-center gap-2 text-xs text-cream/50'>
                                <MoveHorizontal aria-hidden='true' className='size-4 text-gold/70' />
                                برای چرخاندن بسته، آن را بکشید
                            </p>
                        </div>

                        <div className='lg:col-span-6'>
                            <div role='group' aria-label='انتخاب محصول' className='flex flex-wrap gap-2.5'>
                                {products.map(product => {
                                    const active = product.id === selected.id

                                    return (
                                        <button
                                            key={product.id}
                                            type='button'
                                            aria-pressed={active}
                                            onClick={() => setSelectedId(product.id)}
                                            className={cn(
                                                'inline-flex h-11 items-center rounded-full border px-5 text-sm font-medium transition-all duration-300',
                                                active
                                                    ? 'border-gold bg-gold text-forest-950 shadow-[0_10px_30px_-12px_rgba(201,162,74,0.8)]'
                                                    : 'border-cream/15 bg-cream/[0.04] text-cream/80 hover:border-gold/60 hover:text-cream'
                                            )}
                                        >
                                            <span data-mdlfy-cms={`products:${product.id}:name`}>
                                                {product.name}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>

                            <div key={selected.id} className='mt-10 animate-in fade-in slide-in-from-bottom-2 duration-500'>
                                {selected.category_name && selected.category_id && (
                                    <p className='inline-flex rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 text-xs font-medium text-gold-light'>
                                        <span data-mdlfy-cms={`categories:${selected.category_id}:name`}>
                                            {selected.category_name}
                                        </span>
                                    </p>
                                )}

                                <h3 className='mt-4 text-4xl font-bold sm:text-5xl'>
                                    <span data-mdlfy-cms={`products:${selected.id}:name`}>
                                        {selected.name}
                                    </span>
                                </h3>

                                {selected.scientific_name && (
                                    <p className='mt-2 font-heading text-xl italic text-gold'>
                                        <span dir='ltr' data-mdlfy-cms={`products:${selected.id}:scientific_name`}>
                                            {selected.scientific_name}
                                        </span>
                                    </p>
                                )}

                                {selected.summary && (
                                    <p
                                        data-mdlfy-cms={`products:${selected.id}:summary`}
                                        className='mt-5 max-w-xl text-base leading-8 text-cream/75 sm:text-lg sm:leading-9'
                                    >
                                        {selected.summary}
                                    </p>
                                )}

                                {selected.origin && (
                                    <p className='mt-5 inline-flex items-center gap-2 rounded-2xl border border-cream/10 bg-cream/5 px-4 py-2.5 text-sm'>
                                        <MapPin aria-hidden='true' className='size-4 text-gold' />
                                        <span className='text-cream/60'>منشأ:</span>
                                        <span data-mdlfy-cms={`products:${selected.id}:origin`}>{selected.origin}</span>
                                    </p>
                                )}

                                <div className='mt-9 flex flex-wrap gap-3'>
                                    <ButtonLink href={`/products/${selected.slug}`} variant='gold' size='lg'>
                                        مشخصات و آنالیز
                                        <ArrowLeft aria-hidden='true' />
                                    </ButtonLink>

                                    <ButtonLink
                                        href={{ pathname: '/contact', query: { product: selected.name } }}
                                        variant='outline-light'
                                        size='lg'
                                    >
                                        استعلام قیمت
                                    </ButtonLink>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className='mt-14 rounded-[2rem] border border-dashed border-cream/15 px-6 py-14 text-center'>
                        <p className='font-heading text-2xl font-bold'>
                            فهرست محصولات به‌زودی در اینجا نمایش داده می‌شود
                        </p>

                        <p className='mx-auto mt-3 max-w-md text-sm leading-7 text-cream/65'>
                            برای دریافت فهرست کامل محصولات و قیمت‌ها، با ما در تماس باشید.
                        </p>

                        <ButtonLink href='/contact' variant='gold' className='mt-7'>
                            درخواست فهرست محصولات
                        </ButtonLink>
                    </div>
                )}
            </div>
        </section>
    )
}
